import { useEffect } from 'react';
import { useSocialQuery } from '../api/queries';

type Props = { af: string | null };

const COSCHEDULE_URL =
  'https://app.coschedule.com/shared/63e6176f-9612-43df-9f45-257449c4a7ad/calendar/1574356';
const ELFSIGHT_WIDGET_ID = '784c4f59-2db7-4f09-a086-f275478b3d3e';

declare global {
  interface Window {
    _elfsightLoaded?: boolean;
  }
}

export default function SocialTab({ af }: Props) {
  const { data, isLoading, error } = useSocialQuery(af);

  // Load Elfsight platform.js once
  useEffect(() => {
    if (window._elfsightLoaded) return;
    const s = document.createElement('script');
    s.src = 'https://elfsightcdn.com/platform.js';
    s.async = true;
    document.head.appendChild(s);
    window._elfsightLoaded = true;
  }, []);

  if (!af) {
    return (
      <div className="tab-placeholder">
        <SocialIcon />
        <h3>Social</h3>
        <p>Select a franchisee to view their social media pages</p>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: '#8a95a5' }}>
        <div className="spinner" style={{ margin: '0 auto 16px' }} />
        Finding social media pages...
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: '#c0392b' }}>
        Error loading social media data
      </div>
    );
  }

  const fbPagePath = data?.facebook
    ? data.facebook.replace(/^https?:\/\/(www\.)?facebook\.com\/?/, '').replace(/\/+$/, '')
    : null;
  const fbPageUrl = fbPagePath ? `https://www.facebook.com/${fbPagePath}` : null;
  const fbPluginUrl = fbPageUrl
    ? `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(
        fbPageUrl,
      )}&tabs=timeline&width=500&height=750&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true`
    : null;

  return (
    <>
      {/* Row 1: CoSchedule calendar */}
      <div style={{ marginBottom: 20, padding: '0 16px' }}>
        <div
          style={{
            background: '#fff',
            borderRadius: 10,
            overflow: 'hidden',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              fontWeight: 600,
              color: '#1e3a5f',
              fontSize: 15,
              borderBottom: '1px solid #e9ecef',
            }}
          >
            Social Media Content Calendar
          </div>
          <div style={{ width: '100%', height: 400, overflow: 'hidden' }}>
            <iframe
              src={COSCHEDULE_URL}
              title="CoSchedule"
              style={{
                width: '143%',
                // Scaled by 0.7 below, so visible height ≈ 572 × 0.7 ≈ 400 (fills
                // the wrapper). Keep the wrapper height and this in sync when
                // resizing: wrapperHeight ≈ iframeHeight × 0.7.
                height: 572,
                border: 'none',
                transform: 'scale(0.7)',
                transformOrigin: 'top left',
              }}
              loading="lazy"
            />
          </div>
        </div>
      </div>

      {/* Row 2: Facebook + Instagram */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 20,
          padding: '0 16px',
          marginBottom: 24,
        }}
      >
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0, color: '#1a2a3a', fontSize: 16, fontWeight: 600 }}>
              <span style={{ color: '#1877f2' }}>●</span> Facebook
            </h3>
            {fbPageUrl && (
              <a
                href={fbPageUrl}
                target="_blank"
                rel="noopener"
                style={{ fontSize: 12, color: '#1877f2', textDecoration: 'none' }}
              >
                Open ↗
              </a>
            )}
          </div>
          {fbPluginUrl ? (
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '12px 16px',
                  background: '#f0f6ff',
                  borderBottom: '1px solid #dce6f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 13, color: '#3a4a5a' }}>
                  Want more page likes? Share your page with clients and partners.
                </span>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fbPageUrl!)}`}
                  target="_blank"
                  rel="noopener"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '7px 14px',
                    background: '#1877f2',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: 6,
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                  Share Page to Get More Likes
                </a>
              </div>
              <iframe
                src={fbPluginUrl}
                title="Facebook timeline"
                width={500}
                height={750}
                style={{ border: 'none', overflow: 'hidden', width: '100%', display: 'block' }}
                scrolling="no"
                frameBorder={0}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          ) : (
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                padding: 48,
                textAlign: 'center',
                color: '#8a95a5',
                height: 750,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <h3 style={{ margin: '0 0 8px' }}>No Facebook Page Found</h3>
              <p>No Facebook link on this franchisee's website</p>
            </div>
          )}
        </div>

        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0, color: '#1a2a3a', fontSize: 16, fontWeight: 600 }}>
              <span style={{ color: '#E1306C' }}>●</span> Amada Corporate Instagram
            </h3>
            <a
              href="https://www.instagram.com/amadaseniorcare/"
              target="_blank"
              rel="noopener"
              style={{ fontSize: 12, color: '#E1306C', textDecoration: 'none' }}
            >
              Open ↗
            </a>
          </div>
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
              overflow: 'hidden',
              padding: 16,
            }}
          >
            <div className={`elfsight-app-${ELFSIGHT_WIDGET_ID}`} data-elfsight-app-lazy />
          </div>
        </div>
      </div>

      {/* Row 3: Other social links */}
      <div style={{ padding: '0 16px 24px' }}>
        <h3 style={{ margin: '0 0 12px', color: '#1a2a3a', fontSize: 16, fontWeight: 700 }}>
          Other Social Links
        </h3>
        <div
          style={{
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            padding: '18px 20px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px 28px',
          }}
        >
          <OtherLink
            href="https://www.youtube.com/@AmadaSeniorCare"
            label="Amada YouTube Channel"
            icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#FF0000">
                <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2C0 8 0 12 0 12s0 4 .5 5.8a3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1c.5-1.8.5-5.8.5-5.8s0-4-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z" />
              </svg>
            }
          />
          <OtherLink
            href="https://x.com/AmadaSeniorCare"
            label="Amada X Account"
            icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#000">
                <path d="M18.9 1.1h3.7l-8.1 9.3L24 22.9h-7.4l-5.8-7.6-6.7 7.6H.4l8.7-9.9L0 1.1h7.6l5.2 6.9 6.1-6.9zm-1.3 19.6h2L6.5 3.2h-2.2l13.3 17.5z" />
              </svg>
            }
          />
          <OtherLink
            href="https://www.linkedin.com/company/amada-senior-care/"
            label="Amada Company LinkedIn Page"
            icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#0A66C2">
                <path d="M20.5 2h-17A1.5 1.5 0 0 0 2 3.5v17A1.5 1.5 0 0 0 3.5 22h17a1.5 1.5 0 0 0 1.5-1.5v-17A1.5 1.5 0 0 0 20.5 2zM8 19H5v-9h3v9zM6.5 8.25A1.75 1.75 0 1 1 8.3 6.5a1.78 1.78 0 0 1-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0 0 13 14.19a.66.66 0 0 0 0 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 0 1 2.7-1.4c1.55 0 3.36.86 3.36 3.66L19 19z" />
              </svg>
            }
          />
        </div>
      </div>
    </>
  );
}

function OtherLink({
  href,
  label,
  icon,
}: {
  href: string;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        color: '#1a2a3a',
        fontSize: 14,
        fontWeight: 600,
        textDecoration: 'none',
      }}
    >
      {icon}
      {label}
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8a95a5" strokeWidth="2">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
      </svg>
    </a>
  );
}

function SocialIcon() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      style={{ opacity: 0.3, marginBottom: 12 }}
    >
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}
